import dayjs from "dayjs";

type RequestType = "create" | "update" | "delete";

interface BuildRequestDescriptionParams {
  type: RequestType;
  start: string;
  end: string | null;
  oldStart?: string;
  oldEnd?: string | null;
  projectName: string;
  description?: string;
}

const UNKNOWN_PROJECT = "Unknown project";

const formatHours = (start: string, end: string | null): string => {
  if (!end) return "In progress";

  const durationMs = dayjs(end).diff(dayjs(start));
  if (Number.isNaN(durationMs) || durationMs < 0) return "In progress";

  const hours = durationMs / (1000 * 60 * 60);
  const rounded = Number(hours.toFixed(2));

  return Number.isInteger(rounded)
    ? `${rounded}h`
    : `${rounded.toFixed(2).replace(/\.?0+$/, "")}h`;
};

const formatLine = (start: string, end: string | null): string => {
  const dateLabel = dayjs(start).format("MMM D, YYYY");
  const startLabel = dayjs(start).format("HH:mm");

  if (!end) {
    return `${dateLabel} | ${startLabel} - In progress`;
  }

  const endLabel = dayjs(end).format("HH:mm");

  return `${dateLabel} | ${startLabel} - ${endLabel} (${formatHours(
    start,
    end,
  )})`;
};

const normalizeProjectName = (projectName?: string): string => {
  if (!projectName) return UNKNOWN_PROJECT;

  const trimmed = projectName.trim();

  if (!trimmed || trimmed.toLowerCase() === "loading...") {
    return UNKNOWN_PROJECT;
  }

  return trimmed;
};

const formatNotes = (description?: string): string => {
  if (!description) return "";

  const trimmed = description.trim();
  if (!trimmed) return "";

  return `Notes:\n${trimmed}`;
};

export function buildRequestDescription({
  type,
  start,
  end,
  oldStart,
  oldEnd,
  projectName,
  description,
}: BuildRequestDescriptionParams): string {
  const normalizedProjectName = normalizeProjectName(projectName);
  const notes = formatNotes(description);

  if (type === "update") {
    const beforeStart = oldStart ?? start;
    const beforeEnd = oldEnd ?? null;

    return [
      "Updated work session",
      "",
      "Before:",
      formatLine(beforeStart, beforeEnd),
      "",
      "After:",
      formatLine(start, end),
      "",
      `Project: ${normalizedProjectName}`,
      notes,
    ]
      .filter(Boolean)
      .join("\n");
  }

  const actionLabel =
    type === "create" ? "Created work session" : "Deleted work session";

  return [
    actionLabel,
    formatLine(start, end),
    `Project: ${normalizedProjectName}`,
    notes,
  ]
    .filter(Boolean)
    .join("\n");
}
